import { useState } from "react";
import { motion } from "framer-motion";
import { Search, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Catalogo", target: "#catalogo", testid: "nav-link-catalogo" },
  { label: "Leadership", target: "#leadership", testid: "nav-link-leadership" },
  { label: "Contatti", target: "#contatti", testid: "nav-link-contatti" },
];

export default function Navbar() {
  const { items, setDrawerOpen, query, setQuery } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-white/80 backdrop-blur-xl"
      data-testid="navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <button
          onClick={() => scrollToId("#hero")}
          className="font-display text-2xl font-extrabold tracking-tighter text-ink"
          data-testid="nav-logo"
        >
          iogama<span className="text-cobalt">.</span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => scrollToId(l.target)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60 transition-colors duration-300 hover:text-ink"
              data-testid={l.testid}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => scrollToId("#catalogo")}
              placeholder="Cerca nel catalogo…"
              className="h-9 w-44 border border-black/10 bg-white/70 pl-9 pr-3 text-xs text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none lg:w-52"
              data-testid="nav-search-input"
            />
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative flex h-9 items-center gap-2 bg-ink px-4 text-white transition-colors duration-300 hover:bg-cobalt"
            data-testid="nav-cart-button"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.18em] sm:inline">Ordine</span>
            {items.length > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-cobalt font-mono text-[10px] font-semibold text-white" data-testid="nav-cart-count">
                {items.length}
              </span>
            )}
          </button>
          <button
            className="flex h-9 w-9 items-center justify-center border border-black/10 text-ink lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-black/10 bg-white/95 px-5 py-4 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => {
                setOpen(false);
                scrollToId(l.target);
              }}
              className="block w-full py-2 text-left font-mono text-xs uppercase tracking-[0.18em] text-ink/70"
              data-testid={`nav-mobile-${l.testid}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </motion.header>
  );
}
